import { useState, useEffect, useMemo } from 'react'
import DashboardLayout from '../common/DashboardLayout'
import CourseVideoTable from '../common/CourseVideoTable'
import CustomTrainingTable from '../common/CustomTrainingTable'
import { Link } from 'react-router-dom'
import { certificateLinkFor, storedUserId } from '../../utils/certificateLink'
import TeacherServices from '../../services/TeacherServices'
import QuizServices from '../../services/QuizServices'
import CertificationServices from '../../services/CertificationServices'
import './MainDashboard.css'

// Determine user role from localStorage
const getUserRole = () => {
  const role = localStorage.getItem("userrole");
  if (role === "admin" || role === '"admin"') return "admin"; // Admin
  if (role === "multi_school_admin") return 1; // Multi-School Admin -> school-admin view (scoped by switcher)
  if (role === "1" || role === 1) return 1; // School Admin
  if (role === "2" || role === 2) return 2; // School Teacher
  if (role === "3" || role === 3) return 3; // Individual Teacher
  return 3; // Default to Individual Teacher
};

// Check if user is admin
const isAdmin = (role) => {
  return role === "admin" || role === '"admin"';
};

// Format date for display
const formatDateDisplay = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Check if date is within 30 days
const isDateSoon = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= 30;
};

// Check if date is overdue
const isDateOverdue = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  return date < now;
};

// Sort certifications by a given key
const sortCertifications = (certs, key, direction) => {
  if (!key) return certs;

  return [...certs].sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];

    // Handle null/undefined values - push to end
    if (!aVal && !bVal) return 0;
    if (!aVal) return 1;
    if (!bVal) return -1;

    // Convert to dates for date fields
    if (key === 'dueByDate' || key === 'renewByDate') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};


const MainDashboard = () => {

  // Only a fallback: rows carry their own owner now. See utils/certificateLink.
  const certificateUserId = useMemo(() => storedUserId(), []);

  const [courseStatus, setCourseStatus] = useState({
    isCompleted: false,
    inProgress: false,
    progress: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [courseVideos, setCourseVideos] = useState([]);
  const [customTrainingCourses, setCustomTrainingCourses] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'dueByDate', direction: 'asc' });
  const [userRole, setUserRole] = useState(3);
  const [userType, setUserType] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    // Get user role and type
    const role = getUserRole();
    setUserRole(role);
    setIsAdminUser(isAdmin(role));

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setUserType(user?.type || null);
  }, []);

  useEffect(() => {
    const fetchCourseStatus = async () => {
      try {
        setIsLoading(true);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const teacherId = user?._id || user?.id;

        // Fetch course videos and check completion status
        try {
          const videosResponse = await TeacherServices.getVideosWithQuiz(teacherId);
          if (videosResponse) {
            if (videosResponse.videos) {
              // Show all videos - backend should have 8 videos total
              // If less than 8, we'll still number them sequentially
              setCourseVideos(videosResponse.videos);

              // Check progress to mark course as in progress or completed
              try {
                const progressResponse = await QuizServices.checkProcess(teacherId);
                if (progressResponse) {
                  // Check if course is completed (all 8 videos done)
                  if (progressResponse.currentVideoIndex >= 8) {
                    setCourseStatus(prev => ({ ...prev, isCompleted: true }));
                  } else if (progressResponse.currentGroupIndex !== 0 || progressResponse.currentVideoIndex !== 0) {
                    // Course is in progress
                    setCourseStatus(prev => ({
                      ...prev,
                      inProgress: true,
                      progress: progressResponse
                    }));
                  }
                }
              } catch (error) {
                console.log("No progress found:", error);
              }
            }
          }

          // Also check is_quiz_retake for role 2 (school teachers) to allow retakes
          if (videosResponse?.is_quiz_retake) {
            setCourseStatus(prev => ({ ...prev, isCompleted: true }));
          }
        } catch (error) {
          console.log("Course not completed or error:", error);
        }

        // Fetch ALL certifications assigned to this teacher
        try {
          const certResponse = await CertificationServices.getMyCertifications();
          if (certResponse && Array.isArray(certResponse)) {
            // Set all certifications for the comprehensive table
            setCertifications(certResponse);

            // Also filter custom training courses for the CustomTrainingTable
            const customCourses = certResponse.filter(cert =>
              cert.courseKey && cert.courseKey.startsWith('custom-training-')
            );
            setCustomTrainingCourses(customCourses);
          }
        } catch (error) {
          console.log("Error fetching certifications:", error);
        }

      } catch (error) {
        console.error("Error fetching course status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseStatus();
  }, []);

  const getTrainingPath = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const teacherId = user?._id || user?.id || "";

    if (isAdminUser) {
      // Admin - use regular test path
      return "/test/mcq-test";
    } else if (userRole === 3) {
      // Individual teacher
      return "/test/mcq-test";
    } else if (userRole === 2) {
      // School teacher
      if (teacherId) {
        return `/teachertest/mcq-test?teacher_id=${encodeURIComponent(teacherId)}`;
      }
      return "/teachertest/mcq-test";
    } else {
      // School admin (role 1) - can also take courses
      return "/test/mcq-test";
    }
  };

  const trainingPath = getTrainingPath();

  // Determine if a video is unlocked
  const isVideoUnlocked = (videoIndex) => {
    // Admins have all videos unlocked
    if (isAdminUser) {
      return true;
    }

    // If all courses are completed, all videos are unlocked
    if (courseStatus.isCompleted) {
      return true;
    }

    // First video (index 0) is always unlocked
    if (videoIndex === 0) {
      return true;
    }

    // A video is unlocked if the user has progressed to or past it
    if (courseStatus.progress && courseStatus.progress.currentVideoIndex >= videoIndex) {
      return true;
    }

    // Otherwise, the video is locked
    return false;
  };

  // Handler for when a video is selected from CourseVideoTable
  const handleSelectVideo = (videoIndex, groupIndex = 0) => {
    // Check if video is unlocked before navigating
    if (!isVideoUnlocked(videoIndex)) {
      return; // Don't navigate if video is locked
    }
    // Navigate to the training page with the selected video
    // Check if trainingPath already has query parameters
    const separator = trainingPath.includes('?') ? '&' : '?';
    window.location.href = `${trainingPath}${separator}videoIndex=${videoIndex}&groupIndex=${groupIndex}`;
  };

  return (
    <DashboardLayout
      role={userRole}
      userType={userType}
      activeMenu="Dashboard"
      layoutType="topnav"
    >
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading course information...</p>
        </div>
      ) : (
        <>
          {/* Dashboard Grid */}
          <div className="row justify-content-center g-3">
            {/* Course Status Card */}
            <div className="col-lg-4 col-md-6 d-flex">
              <div className={`card main-dashboard-stat-card flex-fill main-dashboard-stat-card-status ${courseStatus.isCompleted ? 'completed' :
                courseStatus.inProgress ? 'in-progress' :
                  'not-started'
                }`}>
                <div className="card-body">
                  <div className="main-dashboard-stat-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="main-dashboard-stat-content">
                    <h5 className="main-dashboard-stat-label">Course Status</h5>
                    <h2 className="main-dashboard-stat-value">
                      {courseStatus.isCompleted ? (
                        <span>Completed</span>
                      ) : courseStatus.inProgress ? (
                        <span>In Progress</span>
                      ) : (
                        <span>Not Started</span>
                      )}
                    </h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Modules Card */}
            <div className="col-lg-4 col-md-6 d-flex">
              <div className="card main-dashboard-stat-card flex-fill main-dashboard-stat-card-modules">
                <div className="card-body">
                  <div className="main-dashboard-stat-icon">
                    <i className="fas fa-book"></i>
                  </div>
                  <div className="main-dashboard-stat-content">
                    <h5 className="main-dashboard-stat-label">Total Modules</h5>
                    <h2 className="main-dashboard-stat-value">{courseVideos.length || 0}</h2>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Card with Progress Bar */}
            <div className="col-lg-4 col-md-6 d-flex">
              <div className="card main-dashboard-stat-card flex-fill main-dashboard-stat-card-progress">
                <div className="card-body">
                  <div className="main-dashboard-stat-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="main-dashboard-stat-content">
                    <h5 className="main-dashboard-stat-label">Progress</h5>
                    {(() => {
                      const totalCount = courseVideos.length || 1;

                      // If course is completed, show 100%
                      if (courseStatus.isCompleted) {
                        return (
                          <>
                            <h2 className="main-dashboard-stat-value">
                              {totalCount}/{totalCount}
                            </h2>
                            <div className="main-dashboard-progress-bar-container">
                              <div
                                className="main-dashboard-progress-bar-fill"
                                style={{ width: '100%' }}
                              ></div>
                            </div>
                          </>
                        );
                      }

                      // Calculate progress for in-progress courses
                      // currentVideoIndex represents the NEXT video to take (0-based)
                      // So if currentVideoIndex = 3, they've completed videos 0, 1, 2 (3 videos)
                      const currentVideoIndex = courseStatus.progress
                        ? courseStatus.progress.currentVideoIndex
                        : 0;

                      // Number of completed videos is the currentVideoIndex
                      // (since it points to the next uncompleted video)
                      const progressCount = currentVideoIndex;
                      const progressPercentage = totalCount > 0 ? Math.min((progressCount / totalCount) * 100, 100) : 0;

                      return (
                        <>
                          <h2 className="main-dashboard-stat-value">
                            {progressCount}/{totalCount}
                          </h2>
                          {/* Progress Bar */}
                          <div className="main-dashboard-progress-bar-container">
                            <div
                              className="main-dashboard-progress-bar-fill"
                              style={{
                                width: `${progressPercentage}%`
                              }}
                            ></div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /Dashboard Grid */}

          {/* Course Title */}
          <div className="dashboard-title mt-4">
            <h4>Active Shooter and Emergency Protocols Certification</h4>
            <p className="text-muted">Select a module below to begin or continue your training</p>
          </div>

          {/* Course Modules List - Using CourseVideoTable component */}
          {courseVideos.length > 0 && (
            <div className="mt-4">
              <CourseVideoTable
                videos={courseVideos}
                onSelectVideo={handleSelectVideo}
                showCloseButton={false}
                isVideoUnlocked={isVideoUnlocked}
              />
            </div>
          )}

          {/* Custom Training Courses Section */}
          {customTrainingCourses.length > 0 && (
            <>
              <div className="dashboard-title mt-5">
                <h4>Additional Training Courses</h4>
                <p className="text-muted">Complete these courses assigned by your school</p>
              </div>
              <div className="mt-4">
                <CustomTrainingTable courses={customTrainingCourses} />
              </div>
            </>
          )}

          {/* All Assigned Certifications Section with Dates */}
          {certifications.length > 0 && (
            <div className="mt-5">
              <div className="dashboard-title mb-3">
                <h4>My Assigned Certifications</h4>
                <p className="text-muted">Track all your certification requirements, due dates, and renewal dates</p>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Course</th>
                      <th>Status</th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                          const newDirection = sortConfig.key === 'dueByDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc';
                          setSortConfig({ key: 'dueByDate', direction: newDirection });
                        }}
                      >
                        Due Date {sortConfig.key === 'dueByDate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => {
                          const newDirection = sortConfig.key === 'renewByDate' && sortConfig.direction === 'asc' ? 'desc' : 'asc';
                          setSortConfig({ key: 'renewByDate', direction: newDirection });
                        }}
                      >
                        Renewal Date {sortConfig.key === 'renewByDate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      <th>Hours</th>
                      <th>Notes</th>
                      <th>Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortCertifications(certifications, sortConfig.key, sortConfig.direction).map((cert, index) => {
                      const isDueSoon = isDateSoon(cert.dueByDate);
                      const isDueOverdue = isDateOverdue(cert.dueByDate);
                      const isRenewalSoon = isDateSoon(cert.renewByDate);
                      const isRenewalOverdue = isDateOverdue(cert.renewByDate);

                      return (
                        <tr key={cert.id || cert._id || index}>
                          <td>
                            <strong>{cert.courseTitle || cert.courseKey}</strong>
                          </td>
                          <td>
                            <span className={`badge ${cert.status === 'completed' || cert.status === 'approved'
                              ? 'bg-success'
                              : cert.status === 'in_progress'
                                ? 'bg-primary'
                                : 'bg-secondary'
                              }`}>
                              {cert.status === 'not_started' ? 'Not Started' :
                                cert.status === 'in_progress' ? 'In Progress' :
                                  cert.status === 'completed' ? 'Completed' :
                                    cert.status === 'approved' ? 'Approved' :
                                      cert.status || 'Not Started'}
                            </span>
                          </td>
                          <td>
                            {cert.dueByDate ? (
                              <div className="d-flex align-items-center">
                                {isDueOverdue && (
                                  <i className="fas fa-exclamation-circle text-danger me-2" title="Overdue"></i>
                                )}
                                {isDueSoon && !isDueOverdue && (
                                  <i className="fas fa-clock text-warning me-2" title="Due soon"></i>
                                )}
                                <span className={isDueOverdue ? 'text-danger fw-bold' : isDueSoon ? 'text-warning fw-bold' : ''}>
                                  {formatDateDisplay(cert.dueByDate)}
                                </span>
                              </div>
                            ) : '—'}
                          </td>
                          <td>
                            {cert.renewByDate ? (
                              <div className="d-flex align-items-center">
                                {isRenewalOverdue && (
                                  <i className="fas fa-exclamation-circle text-danger me-2" title="Renewal overdue"></i>
                                )}
                                {isRenewalSoon && !isRenewalOverdue && (
                                  <i className="fas fa-clock text-warning me-2" title="Renewal due soon"></i>
                                )}
                                <span className={isRenewalOverdue ? 'text-danger fw-bold' : isRenewalSoon ? 'text-warning fw-bold' : ''}>
                                  {formatDateDisplay(cert.renewByDate)}
                                </span>
                              </div>
                            ) : '—'}
                          </td>
                          <td>{cert.hours || '—'}</td>
                          <td>
                            {cert.notes ? (
                              <small className="text-muted">{cert.notes.substring(0, 50)}{cert.notes.length > 50 ? '...' : ''}</small>
                            ) : '—'}
                          </td>
                          <td>
                            {(() => {
                              const href = certificateLinkFor(cert, certificateUserId);
                              return href ? (
                                <Link to={href} className="btn btn-sm btn-outline-primary text-nowrap">
                                  <i className="fas fa-certificate me-1"></i>
                                  View
                                </Link>
                              ) : <span className="text-muted">—</span>;
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <Link to="/my-certifications" className="btn btn-outline-primary">
                  <i className="fas fa-certificate me-2"></i>
                  View All Certifications
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  )
}

export default MainDashboard

